import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, User, Upload, MessageSquare, LineChart } from 'lucide-react';
import { TagInput } from '../common/TagInput';
import { LanguageInput } from '../common/LanguageInput';
import { SocialFrequencyDisplay } from '../common/SocialFrequencyDisplay';
import { AddSocialNetworkModal } from '../common/AddSocialNetworkModal';
import { TabGroup } from '../common/TabGroup';
import { BrandSelector } from '../common/BrandSelector';
import { FormatLengthSelector } from './FormatLengthSelector';
import { AgeDropdown } from './AgeDropdown';
import { Input } from '../common/Input';
import { useBrands } from '../../hooks/useBrands';
import { useMediaApi } from '../../hooks/useMediaApi';
// import { useToast } from '../../hooks/useToast'; // This file doesn't exist yet
import type { PostingFrequency } from '../common/SocialFrequencyDisplay';
import type { Brand } from '../../hooks/useBrands';
import type { PersonaData } from '../../types/personas';
import { BrandDropdown } from '../common/BrandDropdown';
import { isIdeogramUrl, uploadExternalImageUrl, isExternalMediaUrl } from '../../utils/imageUtils';
import { uploadProfileImage, imageUrlToFile } from '../../utils/profileImageUpload';
import { GifLoader } from '../website/GifLoader';
import { ImageProfileUploader } from '../common/ImageProfileUploader';

interface SocialNetwork {
  platform: string;
  pertinence: 'Primary' | 'Secondary' | 'Tertiary';
  frequency: PostingFrequency;
}

interface PersonaFormProps {
  initialData?: Partial<PersonaData>;
  onSubmit: (data: PersonaData) => void | Promise<void>;
  onCancel: () => void;
}

const emptyPersona: PersonaData = {
  name: '',
  jobTitle: '',
  bio: '',
  tags: [],
  age: 28,
  languages: ['en'],
  socialNetworks: [],
  problems: '',
  goals: '',
  motivations: '',
  challenges: '',
  formatLength: [],
  brandIds: [],
  location: '',
  income: '',
  education: '',
  relationship: '',
  quote: '',
  buying_behavior: '',
  brand_affinity: ''
};

const ageRanges = [
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+'
];

const formatLengths = [
  'Short/Reel',
  'Long/Classic',
  'Story/Ephemeral',
  'Live/Stream'
];

const PersonaForm: React.FC<PersonaFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  // const { showToast } = useToast(); // Temporarily commented out
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabFromUrl = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTabFromUrl);
  const [isAddNetworkModalOpen, setIsAddNetworkModalOpen] = useState(false);
  const [persona, setPersona] = useState<PersonaData>({ 
    ...emptyPersona, 
    ...initialData, 
    languages: initialData?.languages && initialData.languages.length > 0 ? initialData.languages : ['en'],
    socialNetworks: initialData?.socialNetworks?.filter(sn => sn && sn.platform) || [] // Ensure valid socialNetworks
  });
  const isEditMode = Boolean(initialData?.id);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [tagInputValue, setTagInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savingStep, setSavingStep] = useState('');
  
  // Fetch brands from the API
  const { data: brandsData, isLoading: isBrandsLoading } = useBrands();
  const brands = brandsData ? (brandsData as any).brands || [] : [];

  // Initialize the media API for image uploads
  const mediaApi = useMediaApi();

  // Define the saving steps for the loading animation
  const savingSteps = [
    { message: t('personas.saving.steps.validating', "Validating persona data..."), duration: 2000 },
    { message: t('personas.saving.steps.processing', "Processing persona data..."), duration: 2000 },
    { message: t('personas.saving.steps.saving', "Saving persona to database..."), duration: 2000 },
    { message: t('personas.saving.steps.finalizing', "Finalizing your persona..."), duration: 2000 },
  ];

  // Set up step animation when saving
  useEffect(() => {
    if (isSaving) {
      // Set first step initially
      setSavingStep(savingSteps[0].message);

      // Set up the step transition
      const intervalId = setInterval(() => {
        setSavingStep((prevStep) => {
          const currentIndex = savingSteps.findIndex(step => step.message === prevStep);
          const nextIndex = (currentIndex + 1) % savingSteps.length;
          return savingSteps[nextIndex].message;
        });
      }, 2000); // Change step every 2 seconds

      return () => clearInterval(intervalId);
    }
  }, [isSaving]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Keep URL in sync with active tab
  useEffect(() => {
    const current = searchParams.get('tab') || 'profile';
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab]);

  // Update local state if user navigates and URL tab changes
  useEffect(() => {
    const tabInUrl = searchParams.get('tab') || 'profile';
    if (tabInUrl !== activeTab) {
      setActiveTab(tabInUrl);
    }
  }, [searchParams]);

  // We now handle external URLs at submission time, not on URL change
  // This ensures the URL is only processed when the user is ready to save
  
  const updatePersona = (field: keyof PersonaData, value: any) => {
    setPersona(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSocialNetwork = (platform: string, frequency: PostingFrequency) => {
    updatePersona('socialNetworks', [
      ...persona.socialNetworks,
      { platform, frequency, pertinence: 'Primary' }
    ]);
  };

  const handleDeleteSocialNetwork = (platform: string) => {
    updatePersona('socialNetworks', 
      persona.socialNetworks.filter(network => network.platform !== platform)
    );
  };

  // Handle tag input key down to add tags on Enter
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInputValue.trim()) {
      e.preventDefault();
      const newTag = tagInputValue.trim();
      
      // Only add if the tag isn't already in the list
      if (!persona.tags.includes(newTag)) {
        updatePersona('tags', [...persona.tags, newTag]);
      }
      
      // Clear the input
      setTagInputValue('');
    }
  };

  const tabs = [
    { id: 'profile', label: t('personas.form.tabs.profile', 'Profile'), icon: <User className="w-4 h-4" /> },
    { id: 'reasons', label: t('personas.form.tabs.reasons', 'Reasons'), icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'behavior', label: t('personas.form.tabs.behavior', 'Behavior'), icon: <LineChart className="w-4 h-4" /> }
  ];

  // Handle form submission with improved image upload
  const handleSubmit = async () => {
    // Start the saving process and show loading state
    setIsSaving(true);
    setSavingStep(t('personas.saving.steps.validating', "Validating persona data..."));
    
    try {
      // Create a copy of the persona data
      const personaToSubmit = { ...persona };
      
      // Get the selected brand ID
      const brandId = persona.brandIds?.[0] || '';
      
      // STEP 1: Process any external image URL - MUST SUCCEED before submission
      if (personaToSubmit.avatarUrl && isExternalMediaUrl(personaToSubmit.avatarUrl)) {
        try {
          setSavingStep(t('personas.saving.steps.processingAvatar', "Processing external image..."));
          console.log('External avatar URL detected, processing before submission:', personaToSubmit.avatarUrl);
          
          // Use the server-side method to process the URL
          const processedUrl = await uploadExternalImageUrl(
            personaToSubmit.avatarUrl,
            mediaApi,
            brandId
          );
          
          if (!processedUrl || processedUrl === personaToSubmit.avatarUrl) {
            console.error('Failed to process external URL - server returned original URL');
            alert(t('personas.form.errors.externalUrlProcessing', 'Could not process external image URL. Please try a different image.'));
            setIsSaving(false);
            return; // Stop submission if processing fails
          }
          
          // Update persona with the processed URL
          console.log('Successfully processed external URL:', processedUrl);
          personaToSubmit.avatarUrl = processedUrl;
        } catch (error) {
          console.error('Failed to process external avatar URL:', error);
          alert(t('personas.form.errors.externalUrlProcessing', 'Could not process external image URL. Please try a different image.'));
          setIsSaving(false);
          return; // Stop submission if processing fails
        }
      }
      
      // STEP 2: Handle direct file uploads
      if (profileImageFile) {
        try {
          setSavingStep(t('personas.saving.steps.uploadingAvatar', "Uploading avatar image..."));
          console.log('Uploading image file:', profileImageFile.name);
          
          // Set uploading state
          setIsUploadingAvatar(true);
          
          // Upload the file to Socialness storage
          const imageUrl = await uploadProfileImage(profileImageFile, brandId);
          
          // Make sure we got a valid URL back
          if (!imageUrl) {
            throw new Error('Upload succeeded but no URL was returned');
          }
          
          // Update the persona data with the new URL
          personaToSubmit.avatarUrl = imageUrl;
          console.log('Image uploaded successfully, URL:', imageUrl);
        } catch (error) {
          console.error('Image upload failed during form submission:', error);
          alert(t('personas.form.errors.uploadFailed', 'Failed to upload image. Please try again.'));
          setIsSaving(false);
          return; // Stop submission if upload fails
        } finally {
          // Reset uploading state
          setIsUploadingAvatar(false);
        }
      }
      
      // STEP 3: Final check - ensure external URLs have been processed
      if (personaToSubmit.avatarUrl && isExternalMediaUrl(personaToSubmit.avatarUrl)) {
        console.error('External URL was not properly processed:', personaToSubmit.avatarUrl);
        alert(t('personas.form.errors.externalUrlNotProcessed', 'External image URL could not be processed. Please try a different image.'));
        setIsSaving(false);
        return; // Stop submission if external URL wasn't processed
      }
      
      // STEP 4: Process the rest of the data
      setSavingStep(t('personas.saving.steps.processing', "Processing persona data..."));
      
      // Convert string fields to arrays, splitting by newlines and trimming
      const toArray = (val: string | string[] | undefined): string[] => {
        if (Array.isArray(val)) return val.map(s => s.trim()).filter(Boolean);
        if (typeof val === 'string' && val) return val.split('\n').map(s => s.trim()).filter(Boolean);
        return [];
      };
      personaToSubmit.problems = toArray(personaToSubmit.problems);
      personaToSubmit.goals = toArray(personaToSubmit.goals);
      personaToSubmit.motivations = toArray(personaToSubmit.motivations);
      personaToSubmit.challenges = toArray(personaToSubmit.challenges);
      
      // Convert brandIds array to a single brandId string
      if (Array.isArray(personaToSubmit.brandIds) && personaToSubmit.brandIds.length > 0) {
        // Take the first selected brand ID
        (personaToSubmit as any).brandId = personaToSubmit.brandIds[0];
      }
      
      // Ensure avatarURL is set (the API expects avatarURL not avatarUrl)
      if (personaToSubmit.avatarUrl) {
        (personaToSubmit as any).avatarURL = personaToSubmit.avatarUrl;
      }
      
      // Ensure buying_behavior and brand_affinity are explicitly included
      (personaToSubmit as any).buying_behavior = personaToSubmit.buying_behavior || '';
      (personaToSubmit as any).brand_affinity = personaToSubmit.brand_affinity || '';
      (personaToSubmit as any).quote = personaToSubmit.quote || '';
      
      // STEP 5: Submit the persona data to Firestore
      setSavingStep(t('personas.saving.steps.saving', "Saving persona to database..."));
      
      // Log the submission data for debugging
      console.log('Submitting persona with data:', {
        ...personaToSubmit,
        avatarUrl: personaToSubmit.avatarUrl ? '[Image URL - Redacted]' : null
      });
      
      // Simulate a minimum delay for better UX
      const startTime = Date.now();
      const minimumLoadingTime = 3000; // 3 seconds
      
      // Finally submit the fully processed data
      await onSubmit(personaToSubmit);

      // Ensure the loading state is shown for at least the minimum time
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minimumLoadingTime) {
        setSavingStep(t('personas.saving.steps.finalizing', "Finalizing your persona..."));
        await new Promise(resolve => setTimeout(resolve, minimumLoadingTime - elapsedTime));
      }
    } catch (error) {
      console.error('Error saving persona:', error);
      alert(t('personas.form.errors.saveFailed', 'Failed to save persona. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      {/* Display GifLoader when saving */}
      {isSaving && (
        <GifLoader
          title={isEditMode ? t('personas.saving.updating', "Updating your persona") : t('personas.saving.creating', "Creating your new persona")}
          currentStep={savingStep}
          videoSrc="/Panda Socialness.ai - Casting a spell.mov"
        />
      )}
      
      <div className="w-[95%] max-w-7xl mx-auto">
        <div className="py-4 md:py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={onCancel}
                className="p-2 rounded-lg hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 font-title">
                  {isEditMode ? persona.name : t('personas.form.createTitle', 'Create Persona')}
                </h1>
                <p className="text-xs md:text-sm text-gray-500">
                  {isEditMode ? t('personas.form.editSubtitle', 'Edit your persona details') : t('personas.form.createSubtitle', 'Create a new target audience persona')}
                </p>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm md:text-base"
            >
              <Save className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              {isEditMode ? t('common.save') : t('personas.form.create', 'Create Persona')}
            </button>
          </div>

          <div className="mb-6">
            <TabGroup
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {activeTab === 'profile' ? (
            <div className="space-y-4 md:space-y-6">
              {/* First Row: Key Information and Format */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                {/* Key Information Card - Full width on mobile, 2/3 on desktop */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.cardTitles.keyInfo', 'Key Information')}</h2>
                  <div className="space-y-4 md:space-y-6">
                    {/* Avatar and Name/Job Title/Age */}  
                    <div className="flex items-start gap-4 md:gap-6 mb-4">
                      {/* Profile Image Upload Area */}
                      <ImageProfileUploader
                        imageFile={profileImageFile}
                        onImageFileChange={setProfileImageFile}
                        existingImageUrl={persona.avatarUrl}
                        isUploading={isUploadingAvatar}
                        size="md"
                        alt={persona.name || 'Persona'}
                      />
                      
                      {/* Name, Age, and Job Title Inputs */}
                      <div className="flex-1 space-y-3 md:space-y-4">
                        {/* Name and Age on the same line */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          {/* Name Input */}
                          <div>
                            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                              {t('personas.form.name')}
                            </label>
                            <input
                              type="text"
                              value={persona.name}
                              onChange={(e) => updatePersona('name', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              placeholder="John Doe"
                            />
                          </div>
                          {/* Age Dropdown */}
                          <div>
                            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                              {t('personas.form.age')}
                            </label>
                            <AgeDropdown
                              value={persona.age}
                              onChange={(value) => updatePersona('age', value)}
                            />
                          </div>
                        </div>
                        {/* Job Title Input - Below Name/Age */}
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            {t('personas.form.jobTitle')}
                          </label>
                          <input
                            type="text"
                            value={persona.jobTitle}
                            onChange={(e) => updatePersona('jobTitle', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Marketing Manager @ Company"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Location and Brand Association on the same line */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {/* Location Input */}
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                          {t('personas.form.location')}
                        </label>
                        <Input
                          value={persona.location}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePersona('location', e.target.value)}
                          placeholder="Enter location"
                        />
                      </div>
                      {/* Brand Association - Moved Here */}
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                          {t('personas.form.brandAssociation', 'Brand Association')}
                        </label>
                        <BrandDropdown
                          brands={brands}
                          selectedBrandId={persona.brandIds?.[0] || ''}
                          onBrandSelect={(brandId) => updatePersona('brandIds', [brandId])}
                          isLoading={isBrandsLoading}
                          showRedirectionCard={false}
                        />
                      </div>
                    </div>

                    {/* Language Input - Moved Below */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        {t('personas.form.languages')}
                      </label>
                      <LanguageInput
                        languages={persona.languages}
                        onLanguagesChange={(updatedLanguages) => updatePersona('languages', updatedLanguages)}
                      />
                    </div>
                  </div>
                </div>

                {/* Format Card - Full width on mobile, 1/3 on desktop */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.cardTitles.format', 'Format')}</h2>
                  <div className="space-y-4 md:space-y-6">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                        {t('personas.form.formatLength')}
                      </label>
                      <FormatLengthSelector
                        selectedFormats={persona.formatLength}
                        onFormatChange={(formats) => updatePersona('formatLength', formats)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Row: Communication and Story */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                {/* Communication Card - Full width on mobile, 1/3 on desktop */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.cardTitles.communication', 'Communication')}</h2>
                  <div className="space-y-4 md:space-y-6">
                    <div>
                      <SocialFrequencyDisplay
                        networks={persona.socialNetworks}
                        onAddNetwork={() => setIsAddNetworkModalOpen(true)}
                        onDeleteNetwork={handleDeleteSocialNetwork}
                        isPersonaForm={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Story Card - Full width on mobile, 2/3 on desktop */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.cardTitles.story', "Persona's Story")}</h2>
                  <div className="space-y-4 md:space-y-6">
                    {/* Bio - Moved to top */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        {t('personas.form.bio')}
                      </label>
                      <textarea
                        value={persona.bio}
                        onChange={(e) => updatePersona('bio', e.target.value)}
                        rows={4}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>

                    {/* Income, Relationship, and Education on the same line (desktop) with custom widths */}
                    <div className="flex flex-col md:flex-row md:gap-6 space-y-4 md:space-y-0">
                      {/* Income (md:w-2/12) */}
                      <div className="md:w-2/12">
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                          {t('personas.form.income')}
                        </label>
                        <Input
                          value={persona.income}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePersona('income', e.target.value)}
                          placeholder="Enter income"
                        />
                      </div>
                      {/* Relationship Status (md:w-4/12) */}
                      <div className="md:w-4/12">
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                          {t('personas.form.relationship')}
                        </label>
                        <Input
                          value={persona.relationship}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePersona('relationship', e.target.value)}
                          placeholder="Enter relationship status"
                        />
                      </div>
                      {/* Education (md:w-6/12) */}
                      <div className="md:w-6/12">
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                          {t('personas.form.education')}
                        </label>
                        <Input
                          value={persona.education}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePersona('education', e.target.value)}
                          placeholder="Enter education"
                        />
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        {t('personas.form.tags')}
                      </label>
                      <TagInput
                        tags={persona.tags}
                        onTagsChange={(tags) => updatePersona('tags', tags)}
                        placeholder={t('personas.form.tagsPlaceholder')}
                        inputValue={tagInputValue}
                        onInputChange={setTagInputValue}
                        onInputKeyDown={handleTagInputKeyDown}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'reasons' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Problems & Challenges Card */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.cardTitles.problemsChallenges', 'Problems & Challenges')}</h2>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">😣</span>
                      <label className="block text-xs md:text-sm font-medium text-gray-700">
                        {t('personas.form.problems')}
                      </label>
                    </div>
                    <textarea
                      value={Array.isArray(persona.problems) ? persona.problems.join('\n') : (persona.problems || '')}
                      onChange={(e) => {
                        updatePersona('problems', e.target.value);
                      }}
                      rows={5}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🔥</span>
                      <label className="block text-xs md:text-sm font-medium text-gray-700">
                        {t('personas.form.challenges')}
                      </label>
                    </div>
                    <textarea
                      value={Array.isArray(persona.challenges) ? persona.challenges.join('\n') : (persona.challenges || '')}
                      onChange={(e) => {
                        updatePersona('challenges', e.target.value);
                      }}
                      rows={5}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Goals & Motivations Card */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.cardTitles.goalsMotivations', 'Goals & Motivations')}</h2>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🎯</span>
                      <label className="block text-xs md:text-sm font-medium text-gray-700">
                        {t('personas.form.goals')}
                      </label>
                    </div>
                    <textarea
                      value={Array.isArray(persona.goals) ? persona.goals.join('\n') : (persona.goals || '')}
                      onChange={(e) => {
                        updatePersona('goals', e.target.value);
                      }}
                      rows={5}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🚀</span>
                      <label className="block text-xs md:text-sm font-medium text-gray-700">
                        {t('personas.form.motivations')}
                      </label>
                    </div>
                    <textarea
                      value={Array.isArray(persona.motivations) ? persona.motivations.join('\n') : (persona.motivations || '')}
                      onChange={(e) => {
                        updatePersona('motivations', e.target.value);
                      }}
                      rows={5}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Quote Card - 1/3 width on desktop */}
              <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.quote', 'Quote')}</h2>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">💬</span>
                      <label className="block text-xs md:text-sm font-medium text-gray-700">
                        {t('personas.form.quote', 'Persona Quote')}
                      </label>
                    </div>
                    <textarea
                      value={persona.quote || ''}
                      onChange={(e) => updatePersona('quote', e.target.value)}
                      rows={6}
                      placeholder={t('personas.form.quoteDescription')}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {t('personas.form.quoteDescription')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Buying Behavior and Brand Affinity - 2/3 width on desktop */}
              <div className="lg:col-span-2 space-y-4 md:space-y-6">
                {/* Buying Behavior Card */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.buyingBehavior', 'Buying Behavior')}</h2>
                  <div className="space-y-4 md:space-y-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🛒</span>
                        <label className="block text-xs md:text-sm font-medium text-gray-700">
                          {t('personas.form.buyingBehaviorLabel')}
                        </label>
                      </div>
                      <textarea
                        value={persona.buying_behavior || ''}
                        onChange={(e) => updatePersona('buying_behavior', e.target.value)}
                        rows={4}
                        placeholder={t('personas.form.buyingBehaviorPlaceholder')}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        {t('personas.form.buyingBehaviorDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Brand Affinity Card */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">{t('personas.form.brandAffinity', 'Brand Affinity')}</h2>
                  <div className="space-y-4 md:space-y-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">❤️</span>
                        <label className="block text-xs md:text-sm font-medium text-gray-700">
                          {t('personas.form.brandAffinity', 'Brand Relationships')}
                        </label>
                      </div>
                      <textarea
                        value={persona.brand_affinity || ''}
                        onChange={(e) => updatePersona('brand_affinity', e.target.value)}
                        rows={4}
                        placeholder={t('personas.form.brandAffinityPlaceholder')}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        {t('personas.form.brandAffinityDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddSocialNetworkModal
        isOpen={isAddNetworkModalOpen}
        onClose={() => setIsAddNetworkModalOpen(false)}
        onAdd={handleAddSocialNetwork}
        existingPlatforms={persona.socialNetworks
          .filter(sn => sn && sn.platform) // Filter out invalid entries
          .map(sn => sn.platform.toLowerCase())
        }
      />
    </div>
  );
};

export default PersonaForm;